import React, { useCallback, useEffect, useMemo } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router'
import { DynamakerApplication, useConfiguratorContext } from '@augmatica/iframe-interface-react'

import { setIsSummaryPanelVisible, setIsSummaryPreviewExpanded } from '../store/ui/uiSlice'
import OverlayControls from '../components/OverlayControls/OverlayControls'
import ToolbarContainer from '../components/Toolbar/ToolbarContainer'
import SummaryPanel from '../components/SummaryPanel/SummaryPanel'

import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from '../hooks'

const CONFIGURATOR_VIEW_ID = 'configurator-view'

const baseRoute = window.location.pathname
const withTrailingSlash = baseRoute.endsWith('/')

const usePreserveQueryParamsNavigate = () => {
  const navigate = useNavigate()
  const { search } = useLocation()

  return (path: string) => {
    navigate(path + search)
  }
}

const Routing: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = usePreserveQueryParamsNavigate()
  const { pathname } = useLocation()

  const { isSummaryPanelVisible } = useAppSelector((state) => state.ui)

  useEffect(() => {
    navigate(
      isSummaryPanelVisible
        ? `${baseRoute}${withTrailingSlash ? '' : '/'}summary${withTrailingSlash ? '/' : ''}`
        : `${baseRoute}`
    )
  }, [isSummaryPanelVisible])

  // Handle back in browser button
  useEffect(() => {
    if (pathname === baseRoute) {
      dispatch(setIsSummaryPanelVisible(false))
    }
  }, [pathname])

  return null
}

interface Props {
  forceDmTestEnv?: boolean
  forceDmProdEnv?: boolean
  dmArgsFromUrl?: Record<string, string>
  awaitProjectLoaded?: boolean
  onRequestSaveProject: any // TODO type correctly
  onDelayedDeliveryChange: (isSelected: boolean) => void
}

const ConfiguratorView: React.FC<Props> = ({
  forceDmTestEnv = false,
  forceDmProdEnv = false,
  dmArgsFromUrl = {},
  awaitProjectLoaded = false,
  onRequestSaveProject,
  onDelayedDeliveryChange,
}) => {
  const dispatch = useAppDispatch()

  const { content, marketCode } = useAppSelector((state) => state.init)
  const { hasBeenFulfilled_fetchPrices_aggregated } = useAppSelector((state) => state.requests)
  const isSummaryPreviewExpanded = useAppSelector((state) => state.ui.isSummaryPreviewExpanded)
  const isSummaryPanelVisible = useAppSelector((state) => state.ui.isSummaryPanelVisible)
  const configurator = useConfiguratorContext()

  const dmArgs = useMemo(() => {
    const args: Record<string, any> = {
      marketCode,
      awaitProjectLoaded,
      'external-ui': true,
      ...(forceDmProdEnv ? { production: true } : {}),
    }

    // A dm_ arg set to false drops the arg entirely, since DM reads a bare "false" as truthy
    Object.entries(dmArgsFromUrl).forEach(([key, value]) => {
      if (value === 'false') {
        delete args[key]
      } else {
        args[key] = value
      }
    })

    return args
  }, [marketCode, awaitProjectLoaded, forceDmProdEnv, dmArgsFromUrl])

  const handleConfiguratorViewClick = useCallback(
    (event: any) => {
      if (isSummaryPreviewExpanded && event.target.id === CONFIGURATOR_VIEW_ID) {
        dispatch(setIsSummaryPreviewExpanded(false))
      }
    },
    [isSummaryPreviewExpanded, dispatch]
  )

  return (
    <Styled.ConfiguratorView
      id={CONFIGURATOR_VIEW_ID}
      isSummaryPreviewExpanded={isSummaryPreviewExpanded}
      $insideSummaryPanel={isSummaryPanelVisible}
      onClick={handleConfiguratorViewClick}
    >
      <Styled.IframeContainer $insideSummaryPanel={isSummaryPanelVisible}>
        {isSummaryPanelVisible && (
          <Styled.SummaryHeader level={1}>{content?.summary_panel?.title}</Styled.SummaryHeader>
        )}

        <DynamakerApplication
          appId={import.meta.env.VITE_DM_APP_ID}
          production={(import.meta.env.VITE_SCENSUM_ENV === 'production' && !forceDmTestEnv) || forceDmProdEnv}
          args={dmArgs}
        />

        {configurator.ready && !isSummaryPanelVisible && <OverlayControls />}

        {!configurator.ready && !isSummaryPanelVisible && (
          <div className="iframe-loader" data-testid="configurator-iframe-loading-indicator">
            <div className="split-cube" />
            <div className="text">{content?.other?.loading}</div>
          </div>
        )}
      </Styled.IframeContainer>

      <BrowserRouter>
        <Routes>
          <Route path={baseRoute} element={<ToolbarContainer />} />
          <Route
            path={`${baseRoute}${withTrailingSlash ? '' : '/'}summary${withTrailingSlash ? '/' : ''}`}
            element={
              hasBeenFulfilled_fetchPrices_aggregated ? (
                <SummaryPanel
                  onRequestSaveProject={onRequestSaveProject}
                  onDelayedDeliveryChange={onDelayedDeliveryChange}
                />
              ) : null
            }
          />
        </Routes>
        <Routing />
      </BrowserRouter>
    </Styled.ConfiguratorView>
  )
}

export default ConfiguratorView
